import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import {
  TenantScreeningService,
  TenantScreening,
  ScreeningRecommendation,
  ScreeningStatus,
  CheckResult,
} from 'src/shared/services/tenant-screening.service';
import { RentalUnitsService } from 'src/shared/services/rental-units.service';

@Component({
  templateUrl: 'screening-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScreeningDetailPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private featureGate = inject(FeatureGateService);
  private screeningService = inject(TenantScreeningService);
  private rentalUnitsService = inject(RentalUnitsService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  screening: TenantScreening;
  loaded = false;
  // Precomputed on load — "Palmetto House · Room A" when the screened person
  // matches a room tenant on a by-room unit; '' otherwise (existing screenings
  // render identically).
  roomContext = '';
  private panelClosedSubscription: Subscription;

  downloadAction = () => this.downloadReport();
  remindAction = () => this.sendReminder();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    this.screening = this.navCtrl.getParam('screening') || params?.screening;
    if (this.isRightSideContent && this.screening) {
      this.rightSidePanelService.setDialogPageTitle(this.screening.applicant_name);
    }
    if (this.screening?.email) {
      this.roomContext = await this.findRoomContext(this.screening.email);
    }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  private async findRoomContext(email: string): Promise<string> {
    const units = await this.rentalUnitsService.getUnits();
    for (const unit of units) {
      if (!this.rentalUnitsService.isByRoom(unit)) { continue; }
      const space = (unit.spaces || []).find(s => s.tenant_email?.toLowerCase() === email.toLowerCase());
      if (space) { return `${unit.property_name} · ${space.name}`; }
    }
    return '';
  }

  recommendationLabel(rec: ScreeningRecommendation): string { return this.screeningService.recommendationLabel(rec); }
  recommendationColor(rec: ScreeningRecommendation): string { return this.screeningService.recommendationColor(rec); }
  statusColor(status: ScreeningStatus): string { return this.screeningService.statusColor(status); }
  creditBand(score: number | null): string { return this.screeningService.creditBand(score); }
  checkColor(value: CheckResult): string { return this.screeningService.checkColor(value); }
  checkLabel(value: CheckResult): string { return this.screeningService.checkLabel(value); }

  downloadReport(): void {
    this.featureGate.gate();
  }

  sendReminder(): void {
    this.featureGate.gate();
  }

  goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      this.rightSidePanelService.navigateTo(url, params, component);
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
